import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { parse } from 'csv-parse/browser/esm/sync';
import { DateTime } from 'luxon';
import { SubmitHandler } from 'react-hook-form';

import { useLazyGetTripsExportAsCsvQuery } from 'src/common/external/bambi-api/emptyApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { MASReport } from 'src/features/trip/reports/MASReport/MASReport';

import { DetailsFormFields } from '../DetailsFormFields';
import { DetailsForm, useDetailsForm } from '../useDetailsForm';
import { MASReportRecord } from './types';

interface PrintableMASDetailsExportProps {
  onClose: () => void;
}

export function PrintableMASDetailsExport({
  onClose,
}: PrintableMASDetailsExportProps) {
  const [data, setData] = useState<MASReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { handleSubmit, control } = useDetailsForm();
  const [downloadLink, setDownloadLink] = useState<string | null>(null);

  const [trigger] = useLazyGetTripsExportAsCsvQuery();

  const handleFetchData = useCallback(
    async (fields: DetailsForm) => {
      // Trigger will cause a blob download, see emptyApi
      const response = await trigger({
        rangeStart: fields.startDate?.startOf('day').toISO() ?? '',
        rangeEnd: fields.endDate?.endOf('day').toISO() ?? '',
        outputSet: '',
        format: 'pdf',
        status: 'completed',
        payers: fields.selectedPayers?.map((payer) => payer.value).join(','),
        passengers:
          fields.selectedPassengers
            ?.map((passenger) => passenger.value)
            .join(',') ?? '',
        externalTripIds:
          fields.externalTripIdOptions
            ?.map((option) => option.value)
            .join(',') ?? '',
        tripIds: '',
      }).unwrap();

      if (!response) {
        throw new Error('Response missing records');
      }

      const data = parse(response.records as string, {
        columns: true,
      });

      return data as MASReportRecord[];
    },
    [trigger]
  );

  const handleDocumentReady = useCallback((pdfBlob: Blob | null) => {
    if (!pdfBlob) {
      setError('Something unexpected happened, unable to render PDF');
      return;
    }

    setDownloadLink(URL.createObjectURL(pdfBlob));
  }, []);

  const handleDownloadTrips = useCallback<SubmitHandler<DetailsForm>>(
    async (fields) => {
      try {
        setError(undefined);
        setLoading(true);

        const data = await handleFetchData(fields);
        if (!data || !data.length) {
          setError(`No records found for export`);
          setLoading(false);
          return;
        }

        setData(data);
      } catch (e) {
        const errMsg = `There was an error downloading the data. ${formatServerError(
          e
        )}`;
        setError(errMsg);
        setLoading(false);
      }
    },
    [handleFetchData]
  );

  // Step 3 - After PDF is rendered into a blob, present the link to the user.
  // We do this instead of opening it because the length of time between export
  // and render can be longer than is allowable to avoid the popup blocker

  if (downloadLink) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2 text-lg">
          <span className="text-green-500">
            <DocumentCheckIcon width={32} height={32} />
          </span>
          Report ready
        </div>
        <a
          className="text-blue-500 underline"
          download={`gps-audit-log-report-${DateTime.now().toLocaleString(
            DateTime.DATE_MED
          )}.pdf`}
          href={downloadLink}
          onClick={onClose}
        >
          Click here to download the report
        </a>
      </div>
    );
  }

  // Step 2 - After data is fetched, render the report to render everything
  // else. When done, open the blob
  if (data.length) {
    return <MASReport trips={data} onDocumentReady={handleDocumentReady} />;
  }

  // Step 1 - Present filters to download data
  return (
    <div>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl">GPS Audit Log details download</h1>
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
        <DetailsFormFields control={control} includeDateRange includePayers />
        <div className="flex flex-row justify-between gap-4 pt-6">
          <div>
            <Button disabled={loading} onClick={onClose}>
              Cancel
            </Button>
          </div>
          <div className="flex flex-row gap-2">
            <Button
              dataTestId="download-pdf"
              disabled={loading}
              loading={loading}
              variant="primary"
              onClick={handleSubmit(handleDownloadTrips)}
            >
              {loading ? 'Exporting' : 'Print to PDF'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
